/**
 * Types + pure logic for the Organic analytics tab.
 *
 * Row shapes mirror the RPC return columns documented at the top of
 * supabase/migrations/20260923220200_organic_analytics.sql. Postgres bigint /
 * numeric come back from PostgREST as numbers or numeric strings, so every read
 * goes through num().
 *
 * The pool-agnostic types and formatters live in ../tracking-pool/format.ts (shared with the Ads pool
 * tab) and are re-exported here unchanged.
 */

import { Check, n0, num } from '../tracking-pool/format';

export type { WindowDays, FunnelStep, PoolHour, PoolHealth, CheckStatus, Check } from '../tracking-pool/format';
export { num, n0, formatMoney, formatDelta, POSTHOG_PROJECT_URL, posthogPersonUrl } from '../tracking-pool/format';

export interface OrganicSummary {
    window_start: string;
    window_end: string;
    first_time_callers: number;
    first_time_callers_prev: number;
    first_time_callers_answered: number;
    first_time_callers_answered_prev: number;
    gbp_first_time_callers: number;
    gbp_first_time_callers_prev: number;
    organic_leads: number;
    organic_leads_prev: number;
    qualified: number;
    qualified_prev: number;
    won: number;
    won_prev: number;
    revenue_cents: number;
    revenue_cents_prev: number;
    gbp_leads: number;
    gbp_qualified: number;
    gbp_won: number;
    gbp_revenue_cents: number;
    phone_taps: number;
    phone_taps_prev: number;
    phone_tap_people: number;
    posthog_sessions: number | null;
    posthog_sessions_prev: number | null;
    posthog_days: number;
    pool_live: boolean;
    pool_calls: number;
    pool_matched_calls: number;
    pool_unmatched_calls: number;
    pool_junk_calls: number;
    pool_matched_first_time: number;
    coverage_pct: number | null;
    claims: number;
    claims_blocked: number;
    blocking_pct: number | null;
    peak_hour_et: number | null;
    peak_hour_blocking_pct: number | null;
}

export interface CoverageWeek {
    week_start: string;
    matched: number;
    unmatched: number;
    /** Calls to the site-default number (site_phone_lines 'site_default'): organic, never pool-matched. */
    organic_default: number;
    /** Calls to the organic "pool full" line (shown when a claim was blocked): organic, unassigned. */
    organic_pool_full: number;
    canonical_1770: number;
    gbp: number;
    other: number;
    total: number;
    coverage_pct: number | null;
    is_partial: boolean;
}

export interface TrendWeek {
    week_start: string;
    first_time_callers: number;
    first_time_callers_answered: number;
    gbp_first_time_callers: number;
    organic_default_first_time_callers: number;
    pool_matched: number;
    qualified: number;
    won: number;
    phone_taps: number;
    coverage_pct: number | null;
    is_partial: boolean;
}

export interface MatchedCall {
    call_session_id: string;
    called_at: string;
    lead_id: string | null;
    lead_stage: string | null;
    is_qualified: boolean;
    assignment_id: string | null;
    number_e164: string | null;
    arm: string | null;
    tier: string | null;
    trigger_page: string | null;
    landing_page: string | null;
    assignment_language: string | null;
    lead_language: string | null;
    caller_area_code: string | null;
    minutes_after_assignment: number | null;
    candidate_count: number;
    posthog_distinct_id: string | null;
    reasons: string[];
    is_suspect: boolean;
}

export interface ArmRow {
    arm: 'toll_free' | 'local';
    eligible_people: number;
    shown_people: number;
    blocked_claims: number;
    calls: number;
    matched_calls: number;
    leads: number;
    qualified: number;
    won: number;
    revenue_cents: number;
    calls_per_100: number | null;
    qualified_per_100: number | null;
}

export interface EntryRow {
    grain: 'type' | 'page';
    entry_page_type: string;
    page: string | null;
    sessions: number | null;
    phone_taps: number;
    tap_people: number;
    assignments: number;
    calls: number;
    qualified: number;
    won: number;
    revenue_cents: number | null;
}

export const ENTRY_TYPE_LABEL: Record<string, string> = {
    home: 'Home',
    service: 'Service',
    location: 'Location',
    pricing: 'Pricing',
    get_started: 'Get started',
    contact: 'Contact',
    preplanning: 'Pre-planning',
    lp: 'Landing page (/lp)',
    article: 'Articles / guides',
    obituary: 'Obituaries',
    other: 'Other',
};

export const FUNNEL_LABEL: Record<string, string> = {
    sessions: 'Organic sessions (PostHog-measured)',
    eligible: 'Eligible — reached an intent page',
    shown: 'Pool number shown',
    called: 'Called a pool number',
    matched: 'Matched to a visit',
    leads: 'Organic leads',
    qualified: 'Qualified',
    case: 'Case opened',
    won: 'Won (contract signed)',
    revenue: 'Paid',
};

// ── "Is it working?" (plan §10 thresholds) ───────────────────────────────────

/** Minimum matched calls before any rate on the tab is judged (plan §10 "Enough to read"). */
export const MIN_MATCHED = 30;

export interface CheckInputs {
    summary: OrganicSummary | undefined;
    suspects: MatchedCall[] | undefined;
    trend: TrendWeek[] | undefined;
}

/** Mean first-time callers per full week over the last 8 full weeks (the P0 baseline). */
export const baselinePerWeek = (trend: TrendWeek[] | undefined): number | null => {
    const full = (trend ?? []).filter(w => !w.is_partial);
    const last8 = full.slice(-8);
    if (last8.length === 0) return null;
    return last8.reduce((s, w) => s + n0(w.first_time_callers), 0) / last8.length;
};

const pct = (a: number, b: number): number | null => (b > 0 ? (100 * a) / b : null);

export const evaluateChecks = ({ summary, suspects, trend }: CheckInputs): Check[] => {
    const live = !!summary?.pool_live;
    const matched = n0(summary?.pool_matched_calls);
    const unmatched = n0(summary?.pool_unmatched_calls);
    const junk = n0(summary?.pool_junk_calls);
    const enough = matched >= MIN_MATCHED;
    const wait = (key: string, label: string, target: string, value = '—'): Check =>
        ({ key, label, target, status: 'waiting', value });

    const checks: Check[] = [];

    // 1. matched rate
    const poolCalls = matched + unmatched;
    const matchedRate = pct(matched, poolCalls);
    checks.push(!live || !enough || matchedRate == null
        ? wait('matched', 'Pool calls matching exactly one visit', '≥ 95 %',
            live ? `${matched} of ${MIN_MATCHED} matched calls` : '—')
        : {
            key: 'matched', label: 'Pool calls matching exactly one visit', target: '≥ 95 %',
            value: `${matchedRate.toFixed(0)} %`,
            status: matchedRate >= 95 ? 'pass' : matchedRate >= 90 ? 'watch' : 'fail',
        });

    // 2. suspects
    const sus = (suspects ?? []).filter(s => s.is_suspect).length;
    const susRate = pct(sus, matched);
    checks.push(!live || !enough || susRate == null
        ? wait('suspects', 'Wrong-match suspects', '≤ 2 %, reviewed', live ? `${sus} flagged` : '—')
        : {
            key: 'suspects', label: 'Wrong-match suspects', target: '≤ 2 %, reviewed',
            value: `${susRate.toFixed(1)} % · ${sus} call${sus === 1 ? '' : 's'}`,
            status: susRate <= 2 ? 'pass' : susRate <= 5 ? 'watch' : 'fail',
        });

    // 3. blocking at peak hour
    const blk = num(summary?.peak_hour_blocking_pct);
    checks.push(!live || blk == null
        ? wait('blocking', 'Claims blocked at peak hour', '< 2 %')
        : {
            key: 'blocking', label: 'Claims blocked at peak hour', target: '< 2 %',
            value: `${blk.toFixed(1)} %`,
            status: blk < 2 ? 'pass' : blk < 5 ? 'watch' : 'fail',
        });

    // 4. junk
    const junkRate = pct(junk, poolCalls + junk);
    checks.push(!live || junkRate == null || poolCalls + junk < MIN_MATCHED
        ? wait('junk', 'Junk calls on pool numbers', '< 5 %')
        : {
            key: 'junk', label: 'Junk calls on pool numbers', target: '< 5 %',
            value: `${junkRate.toFixed(1)} %`,
            status: junkRate < 5 ? 'pass' : junkRate < 10 ? 'watch' : 'fail',
        });

    // 5. no harm: current full-week callers vs 8-week baseline
    const base = baselinePerWeek(trend);
    const full = (trend ?? []).filter(w => !w.is_partial);
    const lastWeek = full.length ? n0(full[full.length - 1].first_time_callers) : null;
    const drift = base && lastWeek != null ? ((lastWeek - base) / base) * 100 : null;
    checks.push(!live || drift == null
        ? wait('harm', 'First-time callers vs P0 baseline', '±15 %',
            base != null ? `baseline ${base.toFixed(0)}/week` : '—')
        : {
            key: 'harm', label: 'First-time callers vs P0 baseline', target: '±15 %',
            value: `${drift >= 0 ? '+' : ''}${drift.toFixed(0)} %`,
            status: Math.abs(drift) <= 15 ? 'pass' : Math.abs(drift) <= 25 ? 'watch' : 'fail',
        });

    // 6. coverage rising
    const cov = full.map(w => num(w.coverage_pct)).filter((v): v is number => v != null);
    const rising = cov.length >= 2 ? cov[cov.length - 1] - cov[cov.length - 2] : null;
    checks.push(!live || rising == null || cov.every(v => v === 0)
        ? wait('coverage', 'Coverage rising week over week', 'rising')
        : {
            key: 'coverage', label: 'Coverage rising week over week', target: 'rising',
            value: `${cov[cov.length - 2].toFixed(0)} → ${cov[cov.length - 1].toFixed(0)} %`,
            status: rising > 0 ? 'pass' : rising === 0 ? 'watch' : 'fail',
        });

    // 7. enough to read
    checks.push({
        key: 'enough', label: 'Matched calls before drawing conclusions', target: `≥ ${MIN_MATCHED}`,
        value: live ? String(matched) : '—',
        status: live && enough ? 'pass' : 'waiting',
    });

    return checks;
};

// ── A/B arms ─────────────────────────────────────────────────────────────────

/** People per arm needed to see a 50 % difference in call rate at ~3 % (plan §10 "A/B power"). */
export const ARM_MIN_PEOPLE = 2500;

export interface ArmDiff {
    tollFreeRate: number;
    localRate: number;
    diff: number;
    lo: number;
    hi: number;
    enough: boolean;
}

/** Difference in calls per 100 people (toll-free − local) with a 90 % normal interval. */
export const armDifference = (arms: ArmRow[] | undefined): ArmDiff | null => {
    const tf = arms?.find(a => a.arm === 'toll_free');
    const lo = arms?.find(a => a.arm === 'local');
    if (!tf || !lo) return null;
    const n1 = n0(tf.eligible_people);
    const n2 = n0(lo.eligible_people);
    if (n1 === 0 || n2 === 0) return null;
    const p1 = n0(tf.calls) / n1;
    const p2 = n0(lo.calls) / n2;
    const se = Math.sqrt((p1 * (1 - p1)) / n1 + (p2 * (1 - p2)) / n2);
    const d = p1 - p2;
    return {
        tollFreeRate: p1 * 100,
        localRate: p2 * 100,
        diff: d * 100,
        lo: (d - 1.645 * se) * 100,
        hi: (d + 1.645 * se) * 100,
        enough: n1 >= ARM_MIN_PEOPLE && n2 >= ARM_MIN_PEOPLE,
    };
};
